import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Calendar, Download, Users, Package, Tag, Clock, Filter, X, ScanLine, User, FileBarChart, BarChart3, AlertCircle, Boxes, Truck, FileText, Barcode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FOBReports = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('staff-productivity');
  
  // Filters
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Data states
  const [staffProductivityData, setStaffProductivityData] = useState({ records: [], aggregates: {}, total: 0 });
  const [stockInHandData, setStockInHandData] = useState({ records: [], aggregates: {}, total: 0 });
  const [pendingPackingData, setPendingPackingData] = useState({ records: [], aggregates: {}, total: 0 });
  const [usnManagementData, setUsnManagementData] = useState({ documents: [], total: 0 });
  
  // New report data states
  const [bundleWiseData, setBundleWiseData] = useState({ records: [], aggregates: {}, total: 0 });
  const [bundleEntryData, setBundleEntryData] = useState({ records: [], aggregates: {}, total: 0 });
  const [internalTransferData, setInternalTransferData] = useState({ records: [], aggregates: {}, total: 0 });
  const [barcodeDesignData, setBarcodeDesignData] = useState({ records: [], aggregates: {}, total: 0 });
  
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 50;
  
  // USN Popup
  const [showUsnPopup, setShowUsnPopup] = useState(false);
  const [usnPopupData, setUsnPopupData] = useState({ title: '', usns: [], count: 0 });
  const [isLoadingUsns, setIsLoadingUsns] = useState(false);
  
  // USN Tracking
  const [usnTrackingCode, setUsnTrackingCode] = useState('');
  const [usnTrackingData, setUsnTrackingData] = useState(null);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);
  const usnInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [activeTab, page]);

  const fetchData = async () => {
    if (activeTab === 'usn-tracking') return;
    
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (search) params.append('search', search);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      if (activeTab === 'staff-productivity') {
        const response = await apiClient.get(`/fob-reports/staff-productivity?${params}`);
        setStaffProductivityData(response.data);
      } else if (activeTab === 'stock-in-hand') {
        const response = await apiClient.get(`/fob-reports/stock-in-hand?${params}`);
        setStockInHandData(response.data);
      } else if (activeTab === 'pending-packing') {
        const response = await apiClient.get(`/fob-reports/pending-packing?${params}`);
        setPendingPackingData(response.data);
      } else if (activeTab === 'usn-management') {
        const response = await apiClient.get('/sections/fob/usn-assignment-list');
        setUsnManagementData(response.data);
      } else if (activeTab === 'bundle-wise') {
        const response = await apiClient.get(`/fob-reports/bundle-wise?${params}`);
        setBundleWiseData(response.data);
      } else if (activeTab === 'bundle-entry') {
        const response = await apiClient.get(`/fob-reports/bundle-entry?${params}`);
        setBundleEntryData(response.data);
      } else if (activeTab === 'internal-transfer') {
        const response = await apiClient.get(`/fob-reports/internal-transfer?${params}`);
        setInternalTransferData(response.data);
      } else if (activeTab === 'barcode-design') {
        const response = await apiClient.get(`/fob-reports/barcode-design-wise?${params}`);
        setBarcodeDesignData(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchData();
  };

  const clearFilters = () => {
    setSearch('');
    setFromDate('');
    setToDate('');
    setPage(0);
    setTimeout(fetchData, 100);
  };

  const handleViewUsns = async (type, params) => {
    setIsLoadingUsns(true);
    setShowUsnPopup(true);
    try {
      let endpoint = '';
      let title = '';
      
      if (type === 'staff-productivity') {
        endpoint = `/fob-reports/staff-productivity/${params.submission_id}/usns`;
        title = `USNs - Staff: ${params.staff_id}`;
      } else if (type === 'stock-in-hand') {
        endpoint = `/fob-reports/stock-in-hand/${encodeURIComponent(params.barcode)}/usns?operation=${encodeURIComponent(params.operation)}`;
        title = `USNs - ${params.barcode} (${params.operation})`;
      } else if (type === 'pending-packing') {
        endpoint = `/fob-reports/pending-packing-usns?document_barcode=${encodeURIComponent(params.document_barcode)}&sort_no=${encodeURIComponent(params.sort_no)}`;
        title = `Pending USNs - ${params.document_barcode} / ${params.sort_no}`;
      } else if (type === 'bundle-wise') {
        endpoint = `/fob-reports/bundle-wise/${encodeURIComponent(params.bundle_barcode)}/usns`;
        title = `USNs - Bundle: ${params.bundle_barcode}`;
      } else if (type === 'barcode-design') {
        endpoint = `/fob-reports/barcode-design-wise/${encodeURIComponent(params.barcode)}/usns`;
        title = `USNs - Barcode: ${params.barcode}`;
      }
      
      const response = await apiClient.get(endpoint);
      setUsnPopupData({
        title,
        usns: response.data.usns || [],
        count: response.data.count || 0
      });
    } catch (error) {
      console.error('Error fetching USNs:', error);
      toast.error('Failed to load USN details');
      setShowUsnPopup(false);
    } finally {
      setIsLoadingUsns(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);

      let endpoint = '';
      if (activeTab === 'staff-productivity') {
        endpoint = `/fob-reports/export/staff-productivity?${params}`;
      } else if (activeTab === 'stock-in-hand') {
        endpoint = `/fob-reports/export/stock-in-hand?${params}`;
      } else if (activeTab === 'pending-packing') {
        endpoint = `/fob-reports/export/pending-packing?${params}`;
      } else if (activeTab === 'bundle-wise') {
        endpoint = `/fob-reports/export/bundle-wise?${params}`;
      } else if (activeTab === 'bundle-entry') {
        endpoint = `/fob-reports/export/bundle-entry?${params}`;
      } else if (activeTab === 'internal-transfer') {
        endpoint = `/fob-reports/export/internal-transfer?${params}`;
      } else if (activeTab === 'barcode-design') {
        endpoint = `/fob-reports/export/barcode-design-wise?${params}`;
      }

      const response = await apiClient.get(endpoint);
      const { data, filename } = response.data;

      if (!data || data.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Convert to CSV
      const headers = Object.keys(data[0]);
      const rows = data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','));
      const csvContent = [headers.join(','), ...rows].join('\n');

      // Download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    }
  };

  const handleFetchUsnTracking = async () => {
    const code = usnTrackingCode.trim();
    if (!code) {
      toast.error('Please enter or scan a USN code');
      usnInputRef.current?.focus();
      return;
    }

    setIsLoadingTracking(true);
    setUsnTrackingData(null);
    try {
      const response = await apiClient.get(`/fob-reports/usn-tracking/${encodeURIComponent(code)}`);
      setUsnTrackingData(response.data);
      if (!response.data.found) {
        toast.error(response.data.message || 'USN not found');
      }
    } catch (error) {
      console.error('Error fetching USN tracking:', error);
      toast.error('Failed to fetch USN details');
    } finally {
      setIsLoadingTracking(false);
    }
  };

  const handleClearTracking = () => {
    setUsnTrackingCode('');
    setUsnTrackingData(null);
    setTimeout(() => usnInputRef.current?.focus(), 100);
  };

  const handleRevokeUsn = async (entryId, documentBarcode) => {
    if (!window.confirm(`⚠️ REVOKE USN Assignment for Document: ${documentBarcode}?\n\nThis will:\n- Release all assigned USNs back to 'available' status\n- Clear FG Barcode assignments\n- Allow you to re-assign with the FIXED logic\n\nContinue?`)) {
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await apiClient.post(`/sections/fob/revoke-usn-assignment/${entryId}`);
      toast.success(response.data.message || 'USN assignment revoked successfully');
      fetchData(); // Refresh the list
    } catch (error) {
      console.error('Error revoking USN assignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke USN assignment');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const renderFilters = (showDateFilters = true) => (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-4 shadow-sm">
      <div className="flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-sm text-[#5A5F6E] mb-1 block">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Staff ID, Document, Barcode, USN..."
              className="pl-10"
              data-testid="fob-reports-search"
            />
          </div>
        </div>

        {showDateFilters && (
          <>
            <div className="w-[150px]">
              <label className="text-sm text-[#5A5F6E] mb-1 block">From Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="w-[150px]">
              <label className="text-sm text-[#5A5F6E] mb-1 block">To Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </>
        )}

        <Button onClick={handleSearch} className="bg-[#2D5F4F] hover:bg-[#234A3D]">
          <Search className="w-4 h-4 mr-2" />
          Search
        </Button>

        <Button variant="outline" onClick={clearFilters}>
          <Filter className="w-4 h-4 mr-2" />
          Clear
        </Button>

        <Button variant="outline" onClick={handleExport} className="border-green-500 text-green-600 hover:bg-green-50">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>
    </div>
  );

  const renderPagination = (total) => (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5DFD0]">
      <p className="text-sm text-[#5A5F6E]">
        Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(p => Math.max(0, p - 1))}
          disabled={page === 0}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(p => p + 1)}
          disabled={(page + 1) * limit >= total}
        >
          Next
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/dashboard')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="fob-reports-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">FOB Section Reports</h1>
              <p className="text-sm text-[#E8DCC8]">Assortment & Packing Analytics</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(0); }}>
          <TabsList className="mb-4 bg-white border flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="staff-productivity" className="data-[state=active]:bg-[#2D5F4F] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-1" />
              Staff Productivity
            </TabsTrigger>
            <TabsTrigger value="stock-in-hand" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Boxes className="w-4 h-4 mr-1" />
              Stock In Hand
            </TabsTrigger>
            <TabsTrigger value="pending-packing" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <AlertCircle className="w-4 h-4 mr-1" />
              Pending Packing
              {pendingPackingData.total > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingPackingData.aggregates?.total_usns || 0}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="usn-management" className="data-[state=active]:bg-red-600 data-[state=active]:text-white">
              <Tag className="w-4 h-4 mr-1" />
              USN Management
            </TabsTrigger>
            <TabsTrigger value="usn-tracking" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <ScanLine className="w-4 h-4 mr-1" />
              USN Tracking
            </TabsTrigger>
            <TabsTrigger value="bundle-wise" className="data-[state=active]:bg-teal-600 data-[state=active]:text-white" data-testid="bundle-wise-tab">
              <Package className="w-4 h-4 mr-1" />
              Bundle Wise
            </TabsTrigger>
            <TabsTrigger value="bundle-entry" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white" data-testid="bundle-entry-tab">
              <FileText className="w-4 h-4 mr-1" />
              Bundle Entry
            </TabsTrigger>
            <TabsTrigger value="internal-transfer" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="internal-transfer-tab">
              <Truck className="w-4 h-4 mr-1" />
              Internal Transfer
            </TabsTrigger>
            <TabsTrigger value="barcode-design" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white" data-testid="barcode-design-tab">
              <Barcode className="w-4 h-4 mr-1" />
              Barcode/Design
            </TabsTrigger>
          </TabsList>

          {/* Staff Productivity Report */}
          <TabsContent value="staff-productivity">
            {renderFilters()}
            
            {/* Aggregates */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">No of Staffs</p>
                    <p className="text-2xl font-bold text-blue-900">{staffProductivityData.aggregates?.staff_count || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">No of Submissions</p>
                    <p className="text-2xl font-bold text-green-900">{staffProductivityData.aggregates?.bundle_count || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Tag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-700">No of USNs</p>
                    <p className="text-2xl font-bold text-purple-900">{staffProductivityData.aggregates?.usn_count || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : staffProductivityData.records?.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No records found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff Name</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Timestamp</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {staffProductivityData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-medium">{record.staff_id}</td>
                            <td className="px-4 py-3">{record.staff_name || '-'}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleViewUsns('staff-productivity', { submission_id: record.submission_id, staff_id: record.staff_id })}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                                data-testid={`view-usns-${record.submission_id}`}
                              >
                                {record.usn_count}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">{formatDate(record.timestamp)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(staffProductivityData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>

          {/* Stock In Hand Report */}
          <TabsContent value="stock-in-hand">
            {renderFilters(false)}
            
            {/* Aggregates */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Boxes className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">Total Barcodes</p>
                    <p className="text-2xl font-bold text-blue-900">{stockInHandData.aggregates?.total_barcodes || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Tag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-700">Total USNs In Hand</p>
                    <p className="text-2xl font-bold text-purple-900">{stockInHandData.aggregates?.total_usns || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Operations</p>
                    <p className="text-2xl font-bold text-green-900">{stockInHandData.aggregates?.operation_count || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : stockInHandData.records?.length === 0 ? (
                <div className="p-12 text-center text-green-600">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="font-semibold">No stock in hand!</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Barcode</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Design Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Operation Name</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">No of USNs</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {stockInHandData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-mono text-sm font-medium">{record.barcode}</td>
                            <td className="px-4 py-3">{record.design_name || '-'}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                record.operation_name === 'USN Tagging (Op 2)' ? 'bg-yellow-100 text-yellow-800' :
                                record.operation_name === 'Assortment Complete' ? 'bg-blue-100 text-blue-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {record.operation_name}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleViewUsns('stock-in-hand', { barcode: record.barcode, operation: record.operation_name })}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                              >
                                {record.usn_count}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(stockInHandData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>

          {/* Pending Packing Report */}
          <TabsContent value="pending-packing">
            {renderFilters()}
            
            {/* Aggregates */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                    <FileBarChart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-orange-700">Pending Documents</p>
                    <p className="text-2xl font-bold text-orange-900">{pendingPackingData.aggregates?.total_documents || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center">
                    <Tag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-red-700">Pending Sort Nos</p>
                    <p className="text-2xl font-bold text-red-900">{pendingPackingData.aggregates?.total_sort_nos || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-700">Pending USNs</p>
                    <p className="text-2xl font-bold text-purple-900">{pendingPackingData.aggregates?.total_usns || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : pendingPackingData.records?.length === 0 ? (
                <div className="p-12 text-center text-green-600">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="font-semibold">All USNs have been packed!</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Document No</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Document Date</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Vendor Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Sort No</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {pendingPackingData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-orange-50">
                            <td className="px-4 py-3 font-mono font-semibold text-orange-700">{record.document_barcode}</td>
                            <td className="px-4 py-3 text-sm">{record.document_date ? new Date(record.document_date).toLocaleDateString() : '-'}</td>
                            <td className="px-4 py-3">{record.vendor_name}</td>
                            <td className="px-4 py-3 font-mono text-sm">{record.sort_no}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleViewUsns('pending-packing', { document_barcode: record.document_barcode, sort_no: record.sort_no })}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                              >
                                {record.usn_count}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(pendingPackingData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>


          {/* USN Management Tab - NEW: Revoke & Re-assign */}
          <TabsContent value="usn-management">
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-4">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-[#1A1F2E]">USN Assignment Management</h3>
              </div>
              <p className="text-sm text-[#5A5F6E] mb-4">
                Documents with assigned USNs and FG Barcodes. Use &quot;Revoke &amp; Re-assign&quot; to fix duplicate USN issues with the new logic.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Important:</strong> Documents processed before the USN duplication fix may have duplicate USNs across bundles. 
                  Use the &quot;Revoke &amp; Re-assign&quot; button to release the old USNs and allow re-assignment with the corrected sequential distribution logic.
                </p>
              </div>
            </div>

            {isLoading ? (
              <div className="text-center py-12">
                <Clock className="w-12 h-12 mx-auto mb-4 text-[#8A8F9E] animate-spin" />
                <p className="text-[#5A5F6E]">Loading documents...</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F5F1E8] border-b border-[#E5DFD0]">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Document Barcode</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Entry ID</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Total USNs</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Sort Nos</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Bundles</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Assigned At</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Assigned By</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5DFD0]">
                      {usnManagementData.documents?.map((doc) => (
                        <tr key={doc.entry_id} className="hover:bg-[#FFFBF5]">
                          <td className="px-4 py-3 font-mono text-sm font-semibold">{doc.document_barcode}</td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-500">{doc.entry_id}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                              {doc.total_usns_assigned}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-sm">{doc.total_sort_nos}</td>
                          <td className="px-4 py-3 text-center text-sm">{doc.total_bundles}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              doc.status === 'completed' ? 'bg-green-100 text-green-700' :
                              doc.status === 'usn_assigned' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {doc.status === 'usn_assigned' ? 'USN Assigned' : 
                               doc.status === 'completed' ? 'Completed' : doc.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            {formatDate(doc.usn_assigned_at)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{doc.usn_assigned_by || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <Button
                              onClick={() => handleRevokeUsn(doc.entry_id, doc.document_barcode)}
                              variant="outline"
                              size="sm"
                              className="border-red-500 text-red-600 hover:bg-red-50"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Revoke & Re-assign
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {usnManagementData.documents?.length === 0 && (
                  <div className="text-center py-12 text-[#5A5F6E]">
                    <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No documents with USN assignments found</p>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* USN Tracking */}
          <TabsContent value="usn-tracking">
            <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 mb-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <ScanLine className="w-5 h-5 text-purple-600" />
                <h2 className="text-lg font-semibold text-[#1A1F2E]">Track USN Lifecycle</h2>
              </div>
              <p className="text-sm text-[#5A5F6E] mb-4">
                Enter or scan a USN code to view its complete journey through the FOB module including assigned barcode and design name.
              </p>
              
              <div className="flex gap-3 items-end">
                <div className="flex-1 max-w-md">
                  <label className="text-sm text-[#5A5F6E] mb-1 block">USN Code</label>
                  <div className="relative">
                    <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8F9E]" />
                    <Input
                      ref={usnInputRef}
                      value={usnTrackingCode}
                      onChange={(e) => setUsnTrackingCode(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleFetchUsnTracking()}
                      placeholder="Enter or scan USN code..."
                      className="pl-10 font-mono"
                      data-testid="usn-tracking-input"
                    />
                  </div>
                </div>
                <Button 
                  onClick={handleFetchUsnTracking} 
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={isLoadingTracking}
                  data-testid="usn-tracking-fetch-btn"
                >
                  {isLoadingTracking ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Fetching...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Fetch Details
                    </>
                  )}
                </Button>
                {usnTrackingData && (
                  <Button 
                    variant="outline" 
                    onClick={handleClearTracking}
                    data-testid="usn-tracking-clear-btn"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* USN Tracking Results */}
            {usnTrackingData && (
              <div className="space-y-4" data-testid="usn-tracking-results">
                {!usnTrackingData.found ? (
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="p-6 text-center">
                      <X className="w-12 h-12 mx-auto mb-3 text-red-400" />
                      <p className="text-red-700 font-medium">{usnTrackingData.message || 'USN not found'}</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* USN Info Header */}
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center">
                            <Tag className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <p className="text-sm text-purple-700">USN Code</p>
                            <p className="text-xl font-bold font-mono text-purple-900" data-testid="usn-tracking-code-display">
                              {usnTrackingData.usn_code}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Item Details */}
                      <Card className="border-[#E5DFD0]">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3 text-[#2D5F4F]">
                            <FileBarChart className="w-5 h-5" />
                            <h3 className="font-semibold">Item Details</h3>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">FG Barcode:</span>
                              <span className="font-mono font-medium" data-testid="usn-tracking-barcode">{usnTrackingData.fg_barcode || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Design Name:</span>
                              <span className="font-medium" data-testid="usn-tracking-design">{usnTrackingData.design_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Sort No:</span>
                              <span className="font-mono font-medium">{usnTrackingData.sort_no || '-'}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Document Details */}
                      <Card className="border-[#E5DFD0]">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3 text-blue-700">
                            <Package className="w-5 h-5" />
                            <h3 className="font-semibold">Document Details</h3>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Document:</span>
                              <span className="font-mono font-medium">{usnTrackingData.document_barcode || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Vendor:</span>
                              <span className="font-medium">{usnTrackingData.vendor_name || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Vendor Doc No:</span>
                              <span className="font-mono text-sm">{usnTrackingData.vendor_doc_no || '-'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#5A5F6E]">Vendor Doc Date:</span>
                              <span className="text-xs">{usnTrackingData.vendor_doc_date ? new Date(usnTrackingData.vendor_doc_date).toLocaleDateString() : '-'}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Packing Details */}
                      <Card className={`border-[#E5DFD0] ${usnTrackingData.packing?.completed ? 'bg-green-50/50' : 'bg-gray-50'}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3 text-green-700">
                            <Package className="w-5 h-5" />
                            <h3 className="font-semibold">Packing (Op 4)</h3>
                            {usnTrackingData.packing?.completed && (
                              <span className="ml-auto text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">Done</span>
                            )}
                          </div>
                          {usnTrackingData.packing?.completed ? (
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Bundle:</span>
                                <span className="font-mono font-medium">{usnTrackingData.packing.bundle_barcode}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Staff:</span>
                                <span className="font-medium flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {usnTrackingData.packing.staff_id}
                                  {usnTrackingData.packing.staff_name && (
                                    <span className="text-[#5A5F6E] ml-1">({usnTrackingData.packing.staff_name})</span>
                                  )}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#5A5F6E]">Timestamp:</span>
                                <span className="text-xs">{formatDate(usnTrackingData.packing.timestamp)}</span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500 italic">Not yet packed</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Progress Timeline */}
                    <Card className="border-[#E5DFD0]">
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-[#1A1F2E] mb-4">FOB Operations Timeline</h3>
                        <div className="flex items-center justify-between">
                          {/* Op 2: USN Tagging */}
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              usnTrackingData.usn_tagging?.completed ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                              <Tag className="w-5 h-5" />
                            </div>
                            <span className="text-xs mt-2 text-center">USN Tagging<br/>(Op 2)</span>
                          </div>

                          {/* Connector */}
                          <div className={`flex-1 h-1 mx-2 ${
                            usnTrackingData.usn_tagging?.completed ? 'bg-blue-500' : 'bg-gray-200'
                          }`} />

                          {/* Op 3: Assortment */}
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              usnTrackingData.assortment?.completed ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                              <Users className="w-5 h-5" />
                            </div>
                            <span className="text-xs mt-2 text-center">Assortment<br/>(Op 3)</span>
                          </div>

                          {/* Connector */}
                          <div className={`flex-1 h-1 mx-2 ${
                            usnTrackingData.assortment?.completed ? 'bg-purple-500' : 'bg-gray-200'
                          }`} />

                          {/* Op 4: Packing */}
                          <div className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              usnTrackingData.packing?.completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                            }`}>
                              <Package className="w-5 h-5" />
                            </div>
                            <span className="text-xs mt-2 text-center">Packing<br/>(Op 4)</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </>
                )}
              </div>
            )}

            {/* Empty State */}
            {!usnTrackingData && !isLoadingTracking && (
              <Card className="bg-gray-50 border-dashed border-2 border-gray-200">
                <CardContent className="p-12 text-center">
                  <ScanLine className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-[#5A5F6E] mb-2">No USN searched yet</p>
                  <p className="text-sm text-gray-400">Enter a USN code above to track its complete lifecycle in FOB</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Bundle Wise Report */}
          <TabsContent value="bundle-wise" data-testid="bundle-wise-content">
            {renderFilters()}
            
            {/* Aggregates */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-500 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-teal-700">Total Bundles</p>
                    <p className="text-2xl font-bold text-teal-900">{bundleWiseData.aggregates?.total_bundles || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <Tag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Total USNs</p>
                    <p className="text-2xl font-bold text-green-900">{bundleWiseData.aggregates?.total_usns || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">Total Staff</p>
                    <p className="text-2xl font-bold text-blue-900">{bundleWiseData.aggregates?.total_staff || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : bundleWiseData.records?.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No packing records found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="bundle-wise-table">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Sort No</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Design Name</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Bundle No</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Timestamp (IST)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {bundleWiseData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-medium">{record.staff_id}</td>
                            <td className="px-4 py-3">{record.sort_no}</td>
                            <td className="px-4 py-3">{record.design_name || '-'}</td>
                            <td className="px-4 py-3 font-mono text-sm">{record.bundle_barcode}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleViewUsns('bundle-wise', { bundle_barcode: record.bundle_barcode })}
                                className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-semibold hover:bg-teal-200 cursor-pointer underline"
                              >
                                {record.usn_count}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">{record.timestamp_ist}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(bundleWiseData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>

          {/* Bundle Entry Report */}
          <TabsContent value="bundle-entry" data-testid="bundle-entry-content">
            {renderFilters()}
            
            {/* Aggregates */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-cyan-700">Total Entries</p>
                    <p className="text-2xl font-bold text-cyan-900">{bundleEntryData.aggregates?.total_entries || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Total Bundles</p>
                    <p className="text-2xl font-bold text-green-900">{bundleEntryData.aggregates?.total_bundles || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-700">Total Staff</p>
                    <p className="text-2xl font-bold text-blue-900">{bundleEntryData.aggregates?.total_staff || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : bundleEntryData.records?.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No bundle entry records found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="bundle-entry-table">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Document No</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of Bundles</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Timestamp (IST)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {bundleEntryData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-medium">{record.staff_id}</td>
                            <td className="px-4 py-3 font-mono text-sm">{record.document_barcode}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm font-semibold">
                                {record.bundle_count}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">{record.timestamp_ist}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(bundleEntryData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>

          {/* Internal Transfer Report */}
          <TabsContent value="internal-transfer" data-testid="internal-transfer-content">
            {renderFilters()}
            
            {/* Aggregates */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                    <Truck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-700">Total Transfers</p>
                    <p className="text-2xl font-bold text-amber-900">{internalTransferData.aggregates?.total_transfers || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Total Bundles</p>
                    <p className="text-2xl font-bold text-green-900">{internalTransferData.aggregates?.total_bundles || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Tag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-700">Total USNs</p>
                    <p className="text-2xl font-bold text-purple-900">{internalTransferData.aggregates?.total_usns || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : internalTransferData.records?.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Truck className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No internal transfer records found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="internal-transfer-table">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Staff ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Internal Transfer Doc No</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">To Section Name</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">No of Bundles</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Timestamp (IST)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {internalTransferData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-medium">{record.staff_id}</td>
                            <td className="px-4 py-3 font-mono text-sm">{record.transfer_id}</td>
                            <td className="px-4 py-3">{record.to_section}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold">
                                {record.bundle_count}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">
                                {record.usn_count}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">{record.timestamp_ist}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(internalTransferData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>

          {/* Barcode/Design Wise Report */}
          <TabsContent value="barcode-design" data-testid="barcode-design-content">
            {renderFilters()}
            
            {/* Aggregates */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center">
                    <Barcode className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-indigo-700">Total Barcodes</p>
                    <p className="text-2xl font-bold text-indigo-900">{barcodeDesignData.aggregates?.total_barcodes || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-pink-500 rounded-lg flex items-center justify-center">
                    <FileBarChart className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-pink-700">Total Designs</p>
                    <p className="text-2xl font-bold text-pink-900">{barcodeDesignData.aggregates?.total_designs || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                    <Tag className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Total USNs</p>
                    <p className="text-2xl font-bold text-green-900">{barcodeDesignData.aggregates?.total_usns || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-[#E5DFD0] shadow-sm overflow-hidden">
              {isLoading ? (
                <div className="p-12 text-center text-[#8A8F9E]">Loading...</div>
              ) : barcodeDesignData.records?.length === 0 ? (
                <div className="p-12 text-center text-[#8A8F9E]">
                  <Barcode className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No barcode/design records found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="barcode-design-table">
                      <thead>
                        <tr className="bg-[#F5F1E8]">
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Barcode</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Design Name</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-[#1A1F2E]">Count of USNs</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#1A1F2E]">Timestamp (IST)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5DFD0]">
                        {barcodeDesignData.records?.map((record, idx) => (
                          <tr key={idx} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-3 font-mono text-sm">{record.barcode}</td>
                            <td className="px-4 py-3">{record.design_name || '-'}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleViewUsns('barcode-design', { barcode: record.barcode })}
                                className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-semibold hover:bg-indigo-200 cursor-pointer underline"
                              >
                                {record.usn_count}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#5A5F6E]">{record.timestamp_ist}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination(barcodeDesignData.total || 0)}
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* USN Popup */}
      <Dialog open={showUsnPopup} onOpenChange={setShowUsnPopup}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-blue-500" />
              {usnPopupData.title}
            </DialogTitle>
          </DialogHeader>
          
          {isLoadingUsns ? (
            <div className="p-8 text-center text-gray-500">Loading USNs...</div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-3">
                Total: <span className="font-semibold">{usnPopupData.count}</span> USNs
              </p>
              <div className="max-h-60 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">USN Code</th>
                      <th className="px-3 py-2 text-left">Timestamp</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {usnPopupData.usns?.map((usn, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2 font-mono">{usn.usn_code}</td>
                        <td className="px-3 py-2 text-gray-500 text-xs">{formatDate(usn.timestamp)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FOBReports;
